import { ChangeEvent, ReactElement, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper, TextField,
  Grid
} from "@mui/material";
import { IProductBase } from "../../types";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";

interface IProps {
  products: IProductBase[] | null;
  right: IProductBase[];
  setRight: (right: IProductBase[]) => void;
}

const not = (a: IProductBase[], b: IProductBase[]) => {
  return a.filter((value) => b.indexOf(value) === -1);
};

const intersection = (a: IProductBase[], b: IProductBase[]) => {
  return a.filter((value) => b.indexOf(value) !== -1);
};

const FamilyProductPanel = ({ right, setRight, products }: IProps): ReactElement => {
  const [checked, setChecked] = useState<IProductBase[]>([]);
  const [left, setLeft] = useState<IProductBase[]>([]);
  const [ leftFilter, setLeftFilter ] = useState("");
  const [ rightFilter, setRightFilter ] = useState("");

  useEffect(() => {
    if (products){
      setLeft(not(products, right));
    }
  }, [ products ]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value: IProductBase) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    const newRight = right.concat(left.filter(x => x.reference.toLowerCase().includes(leftFilter.toLowerCase())));
    setRight(newRight);
    setLeft(not(products ?? [], newRight));
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    const newLeft = left.concat(right.filter(x => x.reference.toLowerCase().includes(rightFilter.toLowerCase())));
    setLeft(newLeft);
    setRight(not(products ?? [], newLeft));
  };

  const customList = (items: IProductBase[], filter: string, setFilter: (filter: string) => void) => {

    const onchangeFilter = (event: ChangeEvent<HTMLInputElement>) => setFilter(event.target.value);

    return (
      <div style={{  width: 240, }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Filter"
          value={filter}
          onChange={onchangeFilter}
          fullWidth={true}
          slotProps={{
            input: {
              startAdornment:
              <InputAdornment position="start">
                <SearchIcon/>
              </InputAdornment>
          }}}
        />

        <Paper elevation={0} style={{ width: 240, height: 400, overflow: "auto", marginTop: ".5em" }}>
          <List dense={true} component="div" role="list" >
            {items.filter(x => x.reference.toLowerCase().includes(filter.toLowerCase())).map(value => {
              const labelId = `transfer-list-item-${value}-label`;

              return (
                <ListItemButton key={`value-${value.productID}`} onClick={handleToggle(value)} className="p-0">
                  <ListItemIcon>
                    <Checkbox
                      checked={checked.indexOf(value) !== -1}
                      tabIndex={-1}
                      disableRipple={true}
                      aria-labelledby={labelId}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={value.reference}/>
                </ListItemButton>
              );
            })}
            <ListItem/>
          </List>
        </Paper>
      </div>);
  };

  return (
    <div className="m-1 display-flex-row justify-content-center align-items-center gap-1" >
      {customList(left, leftFilter, setLeftFilter)}
      <Grid sx={{ alignItems: "center" }}>
        <Grid container={true} sx={{ flexDirection: "column", alignItems: "center" }}>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllRight}
            disabled={left.length === 0}
            aria-label="move all right">
            ≫
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
            aria-label="move selected right">
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={rightChecked.length === 0}
            aria-label="move selected left">
            &lt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllLeft}
            disabled={right.length === 0}
            aria-label="move all left">
            ≪
          </Button>
        </Grid>
      </Grid>
      <Grid>{customList(right, rightFilter, setRightFilter)}</Grid>
    </div>
  );
};

export default FamilyProductPanel;
